<template>
  <div class="relative w-full h-full overflow-y-auto p-4">
    <div
      v-if="toast"
      class="rounded-lg text-white font-bold px-6 py-3 w-56 fixed bottom-5 right-5 z-50 transition-transform transform translate-y-0"
      :class="
        toastStatus == 'success'
          ? 'bg-green-600'
          : toastStatus == 'warning'
          ? 'bg-orange-300'
          : 'bg-red-500'
      "
    >
      {{ toast }}
    </div>

    <div v-if="loading" class="bg-white rounded-lg p-4">
      <span class="font-bold">{{ $t('Lädt...') }}</span>
    </div>

    <div v-else-if="!status || status === ''" class="bg-white rounded-lg p-4 max-w-2xl mx-auto">
      <h2 class="text-2xl font-bold mb-4">{{ $t('Ehrenamt bei ev-angel-isch') }}</h2>
      <p class="mb-4">
        {{ $t('Als Ehrenamtskraft können Sie uns in verschiedenen Projekten unterstützen. Nach der Registrierung erhalten Sie Zugriff auf den Ehrenamtsbereich, in dem Sie sich für Projekte bewerben und notwendige Formulare digital ausfüllen können.') }}
      </p>
      <button class="full" @click="requestAccess">{{ $t('Zugang anfragen') }}</button>
    </div>

    <div v-else-if="status === 'Angefragt'" class="bg-white rounded-lg p-4 max-w-2xl mx-auto">
      <h2 class="text-2xl font-bold mb-4">{{ $t('Antrag eingegangen') }}</h2>
      <p class="mb-4">{{ $t('Ihr Antrag auf Zugang zum Ehrenamtsbereich ist bei uns eingegangen. Wir werden diesen in Kürze prüfen.') }}</p>
    </div>

    <div v-else-if="status === 'Abgelehnt'" class="bg-white rounded-lg p-4 max-w-2xl mx-auto">
      <h2 class="text-2xl font-bold mb-4 text-red-600">{{ $t('Antrag abgelehnt') }}</h2>
      <p class="mb-4">{{ $t('Leider wurde Ihr Antrag auf Zugang zum Ehrenamtsbereich abgelehnt. Bitte wenden Sie sich an den Support, falls Sie Fragen haben.') }}</p>
    </div>

    <div v-else-if="status === 'Bestätigt'" class="space-y-4 max-w-4xl mx-auto">
      <div v-if="!customer.ProfilKomplett" class="bg-red-400 text-white rounded-lg p-4">
        <span class="font-bold text-lg">{{ $t('Profil unvollständig') }}</span><br>
        {{ $t('Bitte vervollständigen Sie zunächst Ihre Stammdaten unter') }} <a href="/mydata" class="underline font-bold">{{ $t('Meine Daten') }}</a>.
      </div>

      <div v-else class="space-y-4">
        <div class="bg-white rounded-lg p-4">
          <h2 class="text-xl font-bold mb-4">{{ $t('Formulare') }}</h2>
          
          <!-- Unterschrift erforderlich: non-internal forms that require customer signature -->
          <div v-if="availableForms.filter(f => f.requiresSignature && !f.internal).length > 0">
            <h3 class="font-bold mb-2 text-gray-700">{{ $t('Unterschrift erforderlich') }}</h3>
            <div class="space-y-2 mb-4">
              <div v-for="availableForm in availableForms.filter(f => f.requiresSignature && !f.internal)" :key="availableForm.formType" class="flex items-center justify-between border rounded-lg p-3">
                <div class="flex items-center gap-2">
                  <span class="w-3 h-3 rounded-full inline-block" :class="forms[availableForm.formType]?.status === 'done' ? 'bg-green-500' : 'bg-red-500'"></span>
                  <span class="font-bold">{{ $t(availableForm.displayName) }}</span>
                </div>
                <button v-if="!forms[availableForm.formType]" class="full" @click="openForm(availableForm.formType)">{{ $t('Ausfüllen') }}</button>
                <div v-else-if="forms[availableForm.formType]?.status !== 'done'" class="flex items-center gap-2">
                  <span class="text-orange-600 font-bold">{{ $t('In Bearbeitung') }}</span>
                  <button class="full text-sm" @click="openForm(availableForm.formType)">{{ $t('Fortsetzen') }}</button>
                </div>
                <div v-else class="flex items-center gap-2">
                  <span class="text-green-600 font-bold">{{ $t('Ausgefüllt') }}</span>
                  <button class="full text-sm" @click="viewForm(forms[availableForm.formType].id)">{{ $t('Ansehen') }}</button>
                </div>
              </div>
            </div>
          </div>

          <!-- Sonstige Dokumente: no-signature forms + internal forms -->
          <div v-if="availableForms.filter(f => !f.requiresSignature || f.internal).length > 0">
            <h3 class="font-bold mb-2 text-gray-700">{{ $t('Sonstige Dokumente') }}</h3>
            <div class="space-y-2">
              <div v-for="availableForm in availableForms.filter(f => !f.requiresSignature || f.internal)" :key="availableForm.formType" class="flex items-center justify-between border rounded-lg p-3" :class="availableForm.internal && forms[availableForm.formType]?.status !== 'done' ? 'opacity-60' : ''">
                <div class="flex items-center gap-2">
                  <span class="w-3 h-3 rounded-full inline-block" :class="forms[availableForm.formType]?.status === 'done' ? 'bg-green-500' : (availableForm.internal ? 'bg-gray-400' : 'bg-green-500')"></span>
                  <span class="font-bold">{{ $t(availableForm.displayName) }}</span>
                </div>
                <!-- Internal form not yet filled -->
                <span v-if="availableForm.internal && forms[availableForm.formType]?.status !== 'done'" class="text-gray-400 text-sm">{{ $t('Muss von ev-angel-isch ausgefüllt werden') }}</span>
                <!-- Done (internal or not): show Ansehen -->
                <div v-else-if="forms[availableForm.formType]?.status === 'done'" class="flex items-center gap-2">
                  <span class="text-green-600 font-bold">{{ $t('Ausgefüllt') }}</span>
                  <button class="full text-sm" @click="viewForm(forms[availableForm.formType].id)">{{ $t('Ansehen') }}</button>
                </div>
                <!-- Non-internal, not yet opened -->
                <button v-else class="full" @click="openForm(availableForm.formType)">{{ $t('Ansehen') }}</button>
              </div>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg p-4">
          <h2 class="text-xl font-bold mb-4">{{ $t('Projekt-Teilnahme') }}</h2>
          <div v-if="projects.length === 0" class="text-gray-500">{{ $t('Keine Projekte verfügbar.') }}</div>
          <div v-else class="space-y-3">
            <div class="flex flex-col md:flex-row gap-2 items-end">
              <div class="flex-grow pr-4">
                <label class="block text-sm font-bold mb-1">{{ $t('Projekt') }}</label>
                <select v-model="selectedProjectId" class="w-full border rounded px-3 py-2">
                  <option value="">{{ $t('Bitte auswählen') }}</option>
                  <option v-for="project in availableProjects" :key="project.id" :value="project.id">
                    {{ project.Titel }} {{ project.Ort ? '(' + project.Ort + ')' : '' }}
                  </option>
                </select>
              </div>
              
              <button class="full h-[42px]" :disabled="!selectedProjectId" @click="submitParticipation">{{ $t('Anfragen') }}</button>
            </div>
            <div v-if="Object.keys(requests).length > 0" class="mt-4">
              <h3 class="font-bold text-sm mb-2">{{ $t('Meine Anfragen') }}</h3>
              <div v-for="req in Object.values(requests)" :key="req.id" class="flex items-center justify-between border-b py-2">
                <span class="font-medium">{{ req.projectTitle || req.projectId }}</span>
                <span class="text-sm font-bold" :class="requestStatusClass(req.status)">{{ req.status }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <SignaturePopup
      v-if="showFormPopup"
      :formType="currentFormType"
      :birthdate="customer.Geburtsdatum"
      :previewUrl="currentPreviewUrl"
      :requiresSignature="currentRequiresSignature"
      @close="showFormPopup = false"
      @saved="formSaved"
    />

  </div>
</template>

<script>
import API from "@/API";
import SignaturePopup from "@/components/SignaturePopup.vue";

export default {
  name: "Ehrenamt",
  components: { SignaturePopup },
  props: ["user"],
  data() {
    return {
      loading: true,
      status: "",
      customer: {},
      projects: [],
      requests: {},
      forms: {},
      availableForms: [],
      toast: "",
      toastStatus: "success",
      showFormPopup: false,
      currentFormType: "",
      currentPreviewUrl: "",
      currentRequiresSignature: true,
      selectedProjectId: "",
      participationCompensation: "",
    };
  },
  computed: {
    availableProjects() {
      return this.projects.filter(p => !this.requests[p.id]);
    }
  },
  methods: {
    showToast(msg, status = "success") {
      this.toast = msg;
      this.toastStatus = status;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => { this.toast = ""; }, 10000);
    },
    loadStatus() {
      API.getEhrenamtStatus((r, status) => {
        this.loading = false;
        if (status === 200) {
          this.status = r.Ehrenamtsstatus || "";
          this.customer = r.customer || {};
          if (this.status === "Bestätigt") {
            this.loadProjects();
            this.loadForms();
            this.loadAvailableForms();
          }
        }
      });
    },
    requestAccess() {
      API.requestEhrenamtAccess((r, status) => {
        if (status === 200) {
          this.status = "Angefragt";
          this.showToast("Antrag erfolgreich gestellt");
        } else if (r && r.error) {
          this.showToast(r.error, "error");
        } else {
          this.showToast("Fehler beim Stellen des Antrags", "error");
        }
      });
    },
    loadProjects() {
      API.getEhrenamtProjects((r, status) => {
        if (status === 200) {
          this.projects = r.projects || [];
          const reqs = {};
          for (const key in (r.requests || {})) {
            reqs[key] = r.requests[key];
          }
          this.requests = reqs;
        }
      });
    },
    loadForms() {
      API.getEhrenamtForms((r, status) => {
        if (status === 200) {
          const forms = {};
          for (const f of (r || [])) {
            forms[f.formType] = f;
          }
          this.forms = forms;
        }
      });
    },
    loadAvailableForms() {
      API.getEhrenamtAvailableForms((r, status) => {
        if (status === 200) {
          this.availableForms = r || [];
        }
      });
    },
    openForm(formType) {
      const formConfig = this.availableForms.find(f => f.formType === formType);
      this.currentFormType = formType;
      this.currentPreviewUrl = `${process.env.VUE_APP_BE_URL}/ehrenamt/preview?form_type=${encodeURIComponent(formType)}`;
      this.currentRequiresSignature = formConfig ? formConfig.requiresSignature : true;
      this.showFormPopup = true;
    },
    viewForm(formId) {
      window.open(`${process.env.VUE_APP_BE_URL}/ehrenamt/forms/${formId}/view`, '_blank');
    },
    formSaved() {
      this.showFormPopup = false;
      this.showToast("Formular erfolgreich gespeichert");
      this.loadForms();
    },
    submitParticipation() {
      if (!this.selectedProjectId) return;
      API.requestEhrenamtParticipation(this.selectedProjectId, this.participationCompensation, (r, status) => {
        this.selectedProjectId = "";
        this.participationCompensation = "";
        if (status === 200) {
          this.showToast("Teilnahme angefragt");
          this.loadProjects();
        } else if (r && r.error) {
          this.showToast(r.error, "error");
        } else {
          this.showToast("Fehler beim Anfragen der Teilnahme", "error");
        }
      });
    },
    requestStatusClass(status) {
      if (status === "Bestätigt" || status === "Bezahlt") return "text-green-600";
      if (status === "Abgelehnt") return "text-red-600";
      return "text-orange-500";
    },
  },
  mounted() {
    this.loadStatus();
  },
};
</script>
